import React, { useRef } from 'react';
import PopupState, { bindPopover, bindHover } from 'material-ui-popup-state';
import Popover from '@material-ui/core/Popover';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import SunEditor from 'suneditor-react';
import { Formik, Form } from 'formik';
import useStyles from '../../screens/Main/Main.style';
import 'suneditor/dist/css/suneditor.min.css';
import InfoIcon from '@material-ui/icons/Info';
import EditIcon from '@material-ui/icons/Edit';
import { Typography, Button, Box } from '@material-ui/core';
import align from 'suneditor/src/plugins/submenu/align';
import fontSize from 'suneditor/src/plugins/submenu/fontSize';
import formatBlock from 'suneditor/src/plugins/submenu/formatBlock';
import list from 'suneditor/src/plugins/submenu/list';
import usePractice from '../../hooks/usePractice';
import DOMPurify from 'dompurify';
import moment from 'moment';
import { getLoggedInUserData } from '../../utils/Helper';
import { DEPARTMENT_ADMIN, DEPARTMENT_CORPORATION } from '../../constants/departmentConstants';

export default function InfoPopoverComments({ dashBoardComment }) {
  const classes = useStyles();
  const loggedInUserData = getLoggedInUserData();
  const { updateComment, getDashboardComments } = usePractice();
  const [showCommentEditor, setShowCommentEditor] = React.useState(false);
  const [comment, setComment] = React.useState(dashBoardComment?.comment);

  const sanitizedHTML = DOMPurify.sanitize(comment);
  React.useEffect(() => {
    if (dashBoardComment?.titleText?.toLowerCase() == 'header') {
      const comment = dashBoardComment?.comment;

      // Calculate last year
      const lastYear = new Date().getFullYear() - 1;

      // Regular expression to match [year] or a 4-digit number
      const regex = /\[year\]|\d{4}/g;

      // Replace [year] or any 4-digit number with last year's value
      const updatedComment = comment.replace(regex, lastYear.toString());
      setComment(updatedComment);
    } else {
      setComment(dashBoardComment?.comment);
    }
  }, [dashBoardComment?.comment]);
  const spanRef = useRef(null);
  return (
    <Box className={classes.popoverOuter} display="flex">
      <PopupState variant="popover" popupId="demo-popup-popover">
        {(popupState) => (
          <div>
            <span ref={spanRef} {...bindHover(popupState)} style={{ display: 'flex' }}>
              {(dashBoardComment?.titleText?.toLowerCase() != 'header' ? (
                dashBoardComment?.titleText
              ) : (
                <>
                  {' '}
                  <InfoIcon aria-haspopup="true" className={classes.InfoIconStyle} />
                </>
              )) || ''}
            </span>
            <HoverPopover
              className={classes.TableTooltipBoxText}
              {...bindPopover(popupState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              onMouseLeave={() => {
                popupState.close();
                setShowCommentEditor(false);
              }}
            >
              <Typography
                variant="body1"
                dangerouslySetInnerHTML={{ __html: sanitizedHTML || '' }}
              />

              <Box className={classes.TooltipEditedDate} mt={2}>
                <Typography variant="body1">
                  {dashBoardComment?.comment
                    ? dashBoardComment?.updatedAt
                      ? 'Edited on ' + moment(dashBoardComment.updatedAt).format('MMM D, YYYY')
                      : 'Edited on ' +
                        moment(dashBoardComment.createdAt || new Date()).format('MMM D, YYYY')
                    : 'Please enter comment'}
                </Typography>
                <If
                  condition={
                    loggedInUserData?.department?.departmentTitle === DEPARTMENT_CORPORATION ||
                    loggedInUserData?.department?.departmentTitle === DEPARTMENT_ADMIN
                  }
                >
                  {!showCommentEditor && (
                    <EditIcon
                      onClick={() => {
                        setShowCommentEditor(true);
                        popupState.close();
                      }}
                    />
                  )}
                </If>
              </Box>
            </HoverPopover>
          </div>
        )}
      </PopupState>
      {showCommentEditor && (
        <Popover
          className={classes.TableTooltipBoxText}
          open={showCommentEditor} // Control open state with showCommentEditor
          onClose={() => setShowCommentEditor(false)} // Close popover
          anchorEl={spanRef.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Formik
            enableReinitialize={true}
            initialValues={{
              comment: dashBoardComment?.comment || '',
            }}
            onSubmit={async (values, actions) => {
              let result = null;

              if (dashBoardComment?.id) {
                result = await updateComment({
                  dashBoardCommentId: dashBoardComment?.id,
                  comment: values?.comment,
                });
              } else {
                dashBoardComment['comment'] = values?.comment;
                result = await updateComment({
                  dashBoardCommentObj: dashBoardComment,
                });
              }

              if (result.status) {
                setComment(values?.comment);
                getDashboardComments();
              }

              actions.resetForm();
              setShowCommentEditor(false);
            }}
          >
            {({ submitForm, setFieldValue }) => (
              <Form>
                <Box mt={2} className={classes.TooltipEditor}>
                  <SunEditor
                    autoFocus={true}
                    lang="en"
                    name="comment"
                    onChange={(content) => {
                      setFieldValue('comment', content);
                    }}
                    setContents={sanitizedHTML || ''}
                    setOptions={{
                      showPathLabel: false,
                      placeholder: '',
                      plugins: [
                        fontSize,
                        formatBlock,
                        'bold',
                        'italic',
                        'underline',
                        align,
                        list,
                        'indent',
                      ],
                      buttonList: [
                        ['fontSize', 'formatBlock'],
                        ['bold', 'italic', 'underline'],
                        ['align', 'list', 'indent'],
                      ],
                      fontSize: ['8', '10', '12', '14', '15', '16'],
                      formats: ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
                    }}
                    height="auto"
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Box className={classes.TooltipCancelBtn}>
                    <Button
                      variant="text"
                      onClick={() => {
                        setShowCommentEditor(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                  <Box className={classes.TooltipSaveBtn}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        submitForm();
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Popover>
      )}
    </Box>
  );
}
